import { useEffect, useState, useCallback, useRef } from 'react'
import { api } from '../../lib/api'
import { renderMarkdown } from '../../lib/markdown'
import { useToast } from '../ui/Toast'
import { Button } from '../ui/Button'

const TYPE_LABELS: Record<string, string> = {
  sop: 'SOP', site_doc: 'Site Doc', product_guide: 'Product Guide',
  troubleshooting: 'Troubleshooting', client_facing: 'Client FAQ',
  faq: 'FAQ', onboarding: 'Onboarding',
}

const VISIBILITY_ICONS: Record<string, string> = {
  internal: '\u{1F512}', client: '\u{1F465}', public: '\u{1F310}', restricted: '\u{1F6AB}',
}

interface Section {
  id: string
  section_type: string
  title: string
  content: string | null
  visibility: string
  sort_order: number
  children?: Section[]
}

interface Article {
  id: string
  slug: string
  title: string
  article_type: string
  visibility: string
  status: string
  category_id: string | null
  category_name: string | null
  tags: string | null
  version: number
  view_count: number
  helpful_count: number
  not_helpful_count: number
  created_by: string | null
  updated_at: string
  published_at: string | null
  sections?: Section[]
  links?: { id: string; entity_type: string; entity_id: string }[]
}

interface Props {
  slug: string
  onBack: () => void
  onEdit: () => void
  onCategoryClick?: (categoryId: string) => void
}

export function KBArticleView({ slug, onBack, onEdit, onCategoryClick }: Props) {
  const [article, setArticle] = useState<Article | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTocId, setActiveTocId] = useState<string>('')
  const [voted, setVoted] = useState<'yes' | 'no' | null>(null)
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map())
  const { toast, toastError } = useToast()

  const loadArticle = useCallback(async () => {
    try {
      const res = await api.get<Article>(`/kb/articles/${slug}`)
      setArticle(res)
    } catch (err) {
      toastError('Failed to load article', err)
    } finally {
      setLoading(false)
    }
  }, [slug, toastError])

  useEffect(() => { loadArticle() }, [loadArticle])

  // Intersection observer for TOC highlighting
  useEffect(() => {
    if (!article?.sections?.length) return
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveTocId(entry.target.id)
          }
        }
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
    )
    sectionRefs.current.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [article])

  const handleHelpful = async (helpful: boolean) => {
    if (voted || !article) return
    try {
      await api.post(`/kb/articles/${article.slug}/${helpful ? 'helpful' : 'not-helpful'}`)
      setVoted(helpful ? 'yes' : 'no')
      toast(helpful ? 'Thanks for the feedback!' : 'Thanks — we\'ll improve this article')
    } catch (err) {
      toastError('Failed to record feedback', err)
    }
  }

  const handlePrint = () => window.print()

  const handleShare = () => {
    const url = window.location.href
    navigator.clipboard?.writeText(url)
    toast('Link copied to clipboard')
  }

  if (loading) {
    return (
      <div className="page-enter" style={{ padding: 40 }}>
        <div className="skeleton" style={{ height: 32, width: 300, marginBottom: 16 }} />
        <div className="skeleton" style={{ height: 16, width: 200, marginBottom: 32 }} />
        <div className="skeleton" style={{ height: 200, marginBottom: 16 }} />
        <div className="skeleton" style={{ height: 150 }} />
      </div>
    )
  }

  if (!article) {
    return (
      <div className="page-enter" style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.2 }}>?</div>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Article not found</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>This article may have been deleted or moved.</p>
        <Button onClick={onBack}>Back to Knowledge Base</Button>
      </div>
    )
  }

  const sections = article.sections || []
  const tags = article.tags ? article.tags.split(',').map(t => t.trim()).filter(Boolean) : []

  return (
    <div className="page-enter">
      {/* Breadcrumbs */}
      <div className="kb-breadcrumbs kb-no-print">
        <button onClick={onBack}>Knowledge Base</button>
        <span className="separator">/</span>
        {article.category_name && (
          <>
            <span
              className="kb-category-link"
              onClick={() => { if (onCategoryClick && article.category_id) onCategoryClick(article.category_id) }}
              style={{ cursor: onCategoryClick ? 'pointer' : 'default' }}
            >
              {article.category_name}
            </span>
            <span className="separator">/</span>
          </>
        )}
        <span style={{ color: 'var(--text-primary)' }}>
          {article.title.length > 50 ? article.title.slice(0, 50) + '...' : article.title}
        </span>
      </div>

      <div className="kb-view-layout">
        {/* Main content */}
        <div className="kb-view-content">
          {/* Article header */}
          <div className="kb-article-header">
            <h1 className="kb-article-title">{article.title}</h1>
            <div className="kb-article-header-meta">
              <span className={`kb-type-badge kb-type-${article.article_type}`}>
                {TYPE_LABELS[article.article_type] || article.article_type}
              </span>
              {article.category_name && (
                <>
                  <span className="separator">&middot;</span>
                  <span
                    className="kb-category-link"
                    onClick={() => { if (onCategoryClick && article.category_id) onCategoryClick(article.category_id) }}
                  >
                    {article.category_name}
                  </span>
                </>
              )}
              <span className="separator">&middot;</span>
              <span className={`kb-status kb-status-${article.status}`}>
                <span className="kb-status-dot" />
                {article.status === 'in_review' ? 'In Review' : article.status.charAt(0).toUpperCase() + article.status.slice(1)}
              </span>
              <span className="separator">&middot;</span>
              <span>v{article.version}</span>
              <span className="separator">&middot;</span>
              <span>{article.view_count} views</span>
            </div>
            <div className="kb-article-header-meta" style={{ marginTop: 4 }}>
              <span>Updated {new Date(article.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              <span className="separator">&middot;</span>
              <span>{VISIBILITY_ICONS[article.visibility]} {article.visibility.charAt(0).toUpperCase() + article.visibility.slice(1)}</span>
            </div>
            {tags.length > 0 && (
              <div className="kb-article-tags">
                {tags.map(tag => <span key={tag} className="kb-tag">{tag}</span>)}
              </div>
            )}
            {/* Actions */}
            <div className="kb-no-print" style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <Button onClick={onEdit} size="sm">
                Edit Article
              </Button>
              <Button variant="secondary" onClick={handlePrint} size="sm">
                Print
              </Button>
              <Button variant="secondary" onClick={handleShare} size="sm">
                Share Link
              </Button>
            </div>
          </div>

          {/* Sections */}
          {sections.length === 0 ? (
            <div style={{ color: 'var(--text-tertiary)', padding: '20px 0' }}>
              No content yet. <button onClick={onEdit} style={{ color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Add content</button>
            </div>
          ) : (
            sections.map(section => (
              <div
                key={section.id}
                id={`section-${section.id}`}
                ref={(el) => { if (el) sectionRefs.current.set(`section-${section.id}`, el) }}
                className="kb-section-view"
              >
                <div className="kb-section-heading">
                  <h2>{section.title}</h2>
                  <span className={`kb-type-badge kb-type-${section.section_type}`} style={{ fontSize: 9, padding: '1px 5px' }}>
                    {section.section_type}
                  </span>
                  {section.visibility === 'internal' && (
                    <span className="kb-visibility" title="Internal only">{VISIBILITY_ICONS.internal}</span>
                  )}
                  {section.visibility === 'client' && (
                    <span className="kb-visibility" title="Client visible">{VISIBILITY_ICONS.client}</span>
                  )}
                </div>
                {section.content && (
                  <div
                    className="markdown-content"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(section.content) }}
                  />
                )}
                {/* Child sections */}
                {section.children?.map(child => (
                  <div key={child.id} style={{ marginLeft: 20, marginTop: 16 }}>
                    <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>
                      {child.title}
                      {child.visibility === 'internal' && <span className="kb-visibility" style={{ marginLeft: 6 }}>{VISIBILITY_ICONS.internal}</span>}
                    </h3>
                    {child.content && (
                      <div
                        className="markdown-content"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(child.content) }}
                      />
                    )}
                  </div>
                ))}
              </div>
            ))
          )}

          {/* Helpful voting */}
          <div className="kb-helpful kb-no-print">
            <span>Was this article helpful?</span>
            <button
              className={`kb-helpful-btn ${voted === 'yes' ? 'voted' : ''}`}
              onClick={() => handleHelpful(true)}
              disabled={voted !== null}
            >
              👍 Yes
            </button>
            <button
              className={`kb-helpful-btn ${voted === 'no' ? 'voted' : ''}`}
              onClick={() => handleHelpful(false)}
              disabled={voted !== null}
            >
              👎 No
            </button>
            {(article.helpful_count > 0 || article.not_helpful_count > 0) && (
              <span style={{ fontSize: 12, color: 'var(--text-disabled)' }}>
                {article.helpful_count} found this helpful
              </span>
            )}
          </div>
        </div>

        {/* Sidebar — Table of Contents */}
        <div className="kb-view-sidebar kb-no-print">
          {sections.length > 0 && (
            <div className="kb-toc">
              <div className="kb-toc-title">On this page</div>
              {sections.map(section => (
                <a
                  key={section.id}
                  className={`kb-toc-item ${activeTocId === `section-${section.id}` ? 'active' : ''}`}
                  onClick={() => {
                    const el = sectionRefs.current.get(`section-${section.id}`)
                    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                >
                  {section.title}
                </a>
              ))}
            </div>
          )}

          {/* Linked entities */}
          {article.links && article.links.length > 0 && (
            <div style={{
              padding: 12, background: 'var(--bg-surface)',
              borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)',
            }}>
              <div className="kb-toc-title">Linked Entities</div>
              {article.links.map(link => (
                <div key={link.id} style={{
                  padding: '6px 12px', fontSize: 13, color: 'var(--text-secondary)',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <span style={{ fontSize: 11, color: 'var(--text-disabled)', textTransform: 'capitalize' }}>{link.entity_type}:</span>
                  <span>{link.entity_id.slice(0, 8)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
